<template>
  <div class="mx-auto max-w-4xl items-center justify-between p-6 pt-1 lg:px-8">
    <div class="bg-white rounded-lg shadow-lg p-6 sm:p-8">
      <h1 class="title mb-5">中原大學學生關鍵能力評估問卷</h1>

      <div class="status-panel mb-6">
        <div class="status-panel__header">
          <div>
            <div class="status-panel__label">目前狀態</div>
            <!-- <div class="status-panel__title">關鍵能力問卷</div> -->
          </div>
          <div
            class="status-panel__badge"
            :class="
              statusInfo.is_open
                ? 'status-panel__badge--open'
                : 'status-panel__badge--closed'
            "
          >
            {{ statusInfo.is_open ? "開放填寫" : "未開放" }}
          </div>
        </div>

        <div class="status-panel__body">
          <div class="status-panel__item">
            <div class="status-panel__item-label">學期</div>
            <div class="status-panel__item-value">
              {{ statusInfo.semester || "未設定" }}
            </div>
          </div>
          <div class="status-panel__divider"></div>
          <div class="status-panel__item">
            <div class="status-panel__item-label">開放時間</div>
            <div class="status-panel__item-value">
              {{ formatApplyTime(statusInfo.form_open_date) }} ~
              {{ formatApplyTime(statusInfo.form_close_date) }}
            </div>
          </div>
        </div>

        <div class="status-panel__actions">
          <router-link
            v-if="statusInfo.is_open"
            class="status-panel__cta"
            to="/questionnaire/ability/fill"
          >
            開始填寫
          </router-link>
          <button
            v-else
            type="button"
            class="status-panel__cta status-panel__cta--disabled"
            disabled
          >
            尚未開放
          </button>
        </div>
      </div>

      <div ref="recordsSection">
        <h2 class="text-xl font-bold mb-4 text-gray-800 record-title">
          填寫紀錄
        </h2>

        <div class="records-container">
          <!-- 雷達圖區域 -->
          <div class="radar-section-wrapper">
            <div class="radar-section">
              <div class="radar-header">
                <h3 class="radar-title">
                  <template v-if="selectedSeq === null"> 平均分數 </template>
                  <template v-else>
                    填寫時間:
                    {{ formatApplyTime(getSelectedRecord()?.apply_time) }}
                  </template>
                </h3>
                <div class="score-display">
                  <span class="score-value">
                    {{
                      selectedSeq === null
                        ? calculateOverallAverage()
                        : formatAverageScore(getSelectedRecord()?.avg_score)
                    }}
                  </span>
                </div>
              </div>
              <div class="radar-container">
                <div class="chart-wrapper">
                  <canvas id="main-radar"></canvas>
                  <div
                    v-show="isLoadingRecords"
                    class="radar-overlay radar-loading"
                  >
                    載入中...
                  </div>
                  <div
                    v-show="!isLoadingRecords && records.length === 0"
                    class="radar-overlay radar-empty"
                  >
                    無資料
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 紀錄表格 -->
          <div class="table-wrapper">
            <table class="apple-table">
              <thead>
                <tr>
                  <th style="width: 15%">序號</th>
                  <th style="width: 25%">學期</th>
                  <th style="width: 35%">填寫時間</th>
                  <th style="width: 25%">平均分數</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoadingRecords">
                  <td colspan="4">載入中...</td>
                </tr>
                <tr v-else-if="records.length === 0">
                  <td colspan="4">無資料</td>
                </tr>
                <tr
                  v-else
                  v-for="(item, index) in records"
                  :key="item.seq"
                  class="data-row"
                  :class="{
                    selected: selectedSeq === item.seq,
                    updating: isUpdatingChart,
                  }"
                  @click="selectRecord(item.seq)"
                >
                  <td>{{ records.length - index }}</td>
                  <td>{{ item.semester }}</td>
                  <td>{{ formatApplyTime(item.apply_time) }}</td>
                  <td>{{ formatAverageScore(item.avg_score) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import axios from "axios";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

const statusInfo = ref({
  is_open: false,
  semester: "",
  form_open_date: "",
  form_close_date: "",
  description: "",
});

const records = ref([]);
const isLoadingRecords = ref(false);
const selectedSeq = ref(null);
const mainChart = ref(null);
const isUpdatingChart = ref(false);
const recordsSection = ref(null);
const isScrollLocked = ref(false);
const lastScrollTop = ref(0);
const domainOrder = [
  "問題解決",
  "自主學習",
  "國際移動",
  "跨領域能力",
  "資訊科技與人文關懷",
  "社會參與",
];

const fetchStatus = async () => {
  try {
    const response = await axios.get(
      process.env.VUE_APP_api_url + "/questionnaire/ability/status"
    );
    if (response.data && response.data.status === true && response.data.data) {
      statusInfo.value = {
        ...statusInfo.value,
        ...response.data.data,
        is_open: Boolean(response.data.data.is_open),
      };
    }
  } catch (error) {
    console.error("取得問卷狀態失敗:", error);
  }
};

const fetchRecords = async () => {
  isLoadingRecords.value = true;
  try {
    const stuInfo = JSON.parse(sessionStorage.getItem("stuInfo"));
    if (!stuInfo?.userId) {
      records.value = [];
      return;
    }

    const response = await axios.get(
      process.env.VUE_APP_api_url + "/questionnaire/ability/records",
      {
        params: { student_id: stuInfo.userId },
      }
    );

    if (response.data && response.data.status === true) {
      records.value = response.data.data || [];
      records.value.reverse();
      console.log("載入的紀錄數:", records.value.length);
      if (records.value.length > 0) {
        console.log("第一筆紀錄:", records.value[0]);
        console.log(
          "第一筆紀錄的 domain_scores:",
          JSON.parse(JSON.stringify(records.value[0].domain_scores))
        );
      }
      // 載入完成後創建平均雷達圖
      await nextTick();
      setTimeout(() => {
        createAverageRadarChart();
      }, 100);
    }
  } catch (error) {
    console.error("取得填寫紀錄失敗:", error);
  } finally {
    isLoadingRecords.value = false;
  }
};

const formatApplyTime = (value) => {
  if (!value) {
    return "-";
  }

  let dateValue = null;
  if (typeof value === "string") {
    if (value.includes("T")) {
      dateValue = new Date(value);
    } else {
      dateValue = new Date(value.replace(" ", "T") + "+08:00");
    }
  } else {
    dateValue = new Date(value);
  }

  if (Number.isNaN(dateValue.getTime())) {
    return value;
  }

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(dateValue);
  const partMap = Object.fromEntries(
    parts.map((part) => [part.type, part.value])
  );

  return `${partMap.year}/${partMap.month}/${partMap.day} ${partMap.hour}:${partMap.minute}`;
};

const formatAverageScore = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "-";
  }

  return Number(value).toFixed(2);
};

// 計算所有紀錄的平均分數
const calculateAverageDomainScores = () => {
  if (records.value.length === 0) return {};

  const averageScores = {};
  const domainKeys = domainOrder;

  domainKeys.forEach((key) => {
    const scores = records.value
      .map((record) => {
        const domainScores = record.domain_scores || {};
        return domainScores[key] || 0;
      })
      .filter((score) => score > 0);

    averageScores[key] =
      scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
  });

  console.log("計算的平均分數:", JSON.parse(JSON.stringify(averageScores)));
  return averageScores;
};

// 計算所有紀錄的總平均分數
const calculateOverallAverage = () => {
  if (records.value.length === 0) return "-";

  const sum = records.value.reduce((acc, record) => {
    const avgScore = Number(record.avg_score) || 0;
    return acc + avgScore;
  }, 0);

  return formatAverageScore(sum / records.value.length);
};

// 創建主雷達圖
const createMainRadarChart = (domainScores, label = "分數") => {
  if (isUpdatingChart.value) {
    console.log("圖表更新中，跳過此次請求");
    return;
  }

  isUpdatingChart.value = true;

  const canvas = document.getElementById("main-radar");
  if (!canvas) {
    console.error("找不到 canvas 元素");
    isUpdatingChart.value = false;
    return;
  }

  // 銷毀舊圖表
  if (mainChart.value) {
    try {
      // 停止所有動畫
      if (mainChart.value.stop) {
        mainChart.value.stop();
      }
      mainChart.value.destroy();
      mainChart.value = null;
      console.log("舊圖表已銷毀");
    } catch (error) {
      console.error("銷毀舊圖表失敗:", error);
      mainChart.value = null;
    }
  }

  // 清除 canvas 內容
  try {
    const ctx = canvas.getContext("2d");
    if (ctx) {
      // 保存 canvas 尺寸
      const width = canvas.width;
      const height = canvas.height;
      // 重置 canvas
      canvas.width = width;
      canvas.height = height;
      ctx.clearRect(0, 0, width, height);
      console.log("Canvas 已清除並重置");
    }
  } catch (error) {
    console.error("清除 canvas 失敗:", error);
  }

  // 等待一下確保清理完成
  setTimeout(() => {
    const canvas = document.getElementById("main-radar");
    if (!canvas) {
      console.error("延遲後仍找不到 canvas 元素");
      isUpdatingChart.value = false;
      return;
    }

    // 驗證 canvas 是否真的可用
    if (!canvas.getContext) {
      console.error("Canvas 不支持 getContext");
      isUpdatingChart.value = false;
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.error("無法獲取 canvas context");
      isUpdatingChart.value = false;
      return;
    }

    console.log("Canvas 準備就緒，開始創建圖表");

    const safeDomainScores = JSON.parse(JSON.stringify(domainScores || {}));
    const isMobileView = window.innerWidth <= 768;
    const data = domainOrder.map((domain) => {
      return safeDomainScores[domain] || 0;
    });

    // 處理標籤換行
    const wrappedLabels = domainOrder.map((label) => {
      if (label.length > 6) {
        // 將長標籤分成兩行
        const mid = Math.ceil(label.length / 2);
        return [label.substring(0, mid), label.substring(mid)];
      }
      return label;
    });

    console.log("雷達圖數據:", {
      labels: domainOrder,
      wrappedLabels: wrappedLabels,
      data: data,
      domainScores: safeDomainScores,
    });

    // 檢查數據是否有效
    const hasValidData = data.some((value) => value > 0);
    if (!hasValidData) {
      console.warn("警告: 所有數據都是 0");
    }

    try {
      mainChart.value = new Chart(ctx, {
        type: "radar",
        data: {
          labels: wrappedLabels,
          datasets: [
            {
              label: label,
              data: data,
              backgroundColor: "rgba(45, 179, 179, 0.2)",
              borderColor: "#2db3b3",
              borderWidth: 2,
              pointBackgroundColor: "#2db3b3",
              pointBorderColor: "#fff",
              pointHoverBackgroundColor: "#fff",
              pointHoverBorderColor: "#2db3b3",
              pointRadius: 4,
              pointHoverRadius: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "nearest",
            intersect: true,
          },
          hover: {
            mode: "nearest",
            intersect: true,
          },
          events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"],
          animation: {
            duration: 400,
          },
          layout: {
            padding: {
              top: isMobileView ? 10 : 30,
              bottom: isMobileView ? 10 : 30,
              left: isMobileView ? 20 : 60,
              right: isMobileView ? 20 : 60,
            },
          },
          scales: {
            r: {
              min: 0,
              max: 5,
              ticks: {
                stepSize: 1,
                font: {
                  size: isMobileView ? 12 : 14,
                },
              },
              pointLabels: {
                display: !isMobileView,
                font: {
                  size: 14,
                  weight: "600",
                },
                color: "#1f2937",
                padding: 20,
              },
              grid: {
                color: "#e5e5e5",
              },
              angleLines: {
                color: "#e5e5e5",
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              titleFont: {
                size: 16,
                weight: "bold",
              },
              bodyFont: {
                size: 15,
              },
              padding: 12,
              callbacks: {
                label: function (context) {
                  return context.parsed.r.toFixed(2) + " 分";
                },
              },
            },
          },
        },
      });

      console.log("雷達圖創建成功", mainChart.value);
      console.log("Canvas 元素:", canvas);
      console.log("Canvas 尺寸:", canvas.width, "x", canvas.height);
    } catch (error) {
      console.error("創建雷達圖失敗:", error);
      mainChart.value = null;
    } finally {
      setTimeout(() => {
        isUpdatingChart.value = false;
      }, 150);
    }
  }, 100);
};

// 創建平均雷達圖
const createAverageRadarChart = () => {
  const averageScores = calculateAverageDomainScores();
  console.log("創建平均雷達圖");
  createMainRadarChart(averageScores, "平均分數");
};

// 選擇紀錄
const selectRecord = (seq) => {
  if (isUpdatingChart.value) {
    console.log("圖表正在更新中，忽略點擊");
    return;
  }

  if (selectedSeq.value === seq) {
    // 如果點擊同一筆,取消選擇並顯示平均
    selectedSeq.value = null;
    setTimeout(() => {
      createAverageRadarChart();
    }, 150);
  } else {
    // 選擇新的紀錄
    selectedSeq.value = seq;
    const record = records.value.find((r) => r.seq === seq);
    if (record && record.domain_scores) {
      setTimeout(() => {
        createMainRadarChart(record.domain_scores, "分數");
      }, 150);
    }
  }
};

// 取得選中的紀錄
const getSelectedRecord = () => {
  if (selectedSeq.value === null) return null;
  return records.value.find((r) => r.seq === selectedSeq.value);
};

// 滾動捕捉邏輯
const handleScroll = () => {
  if (!recordsSection.value || isScrollLocked.value) return;

  const currentScrollTop =
    window.pageYOffset || document.documentElement.scrollTop;
  const rect = recordsSection.value.getBoundingClientRect();

  // 只在向下滚动时触发
  const isScrollingDown = currentScrollTop > lastScrollTop.value;
  lastScrollTop.value = currentScrollTop;

  // 当填写纪录区域的顶部到达视口顶部附近时（缩小范围）
  if (isScrollingDown && rect.top <= 50 && rect.top >= -50) {
    isScrollLocked.value = true;

    // 获取元素相对于文档顶部的位置
    const elementTop = recordsSection.value.offsetTop;
    // 滚动到标题位置，留空间
    window.scrollTo({
      top: elementTop - 20,
      behavior: "smooth",
    });

    // 延长锁定时间，防止用户继续滚动突破
    setTimeout(() => {
      isScrollLocked.value = false;
    }, 1500);
  }
};

onMounted(async () => {
  await fetchStatus();
  await fetchRecords();

  // 初始化滚动位置
  lastScrollTop.value =
    window.pageYOffset || document.documentElement.scrollTop;

  // 添加滚动监听
  window.addEventListener("scroll", handleScroll, { passive: true });
});

onBeforeUnmount(() => {
  // 組件銷毀前清理圖表
  if (mainChart.value) {
    mainChart.value.destroy();
    mainChart.value = null;
  }

  // 移除滚动监听
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.status-panel {
  @apply bg-white border border-main-primary shadow-lg;
  border-radius: 12px;
  padding: 20px 24px;
}

.status-panel__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.status-panel__label {
  @apply text-main-primary;
  font-size: 0.9rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.status-panel__title {
  color: #000000;
  font-size: 1.4rem;
  font-weight: 700;
  margin-top: 4px;
}

.status-panel__badge {
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.status-panel__badge--open {
  @apply text-main-primary bg-white border border-main-primary;
}

.status-panel__badge--closed {
  @apply text-black bg-gray-300 border border-white;
}

.status-panel__body {
  margin-top: 16px;
  display: grid;
  grid-template-columns: 1fr auto 2fr;
  align-items: center;
  gap: 16px;
  @apply bg-gray-200;
  border-radius: 10px;
  padding: 16px 18px;
}

.status-panel__item-label {
  color: #000000;
  font-size: 0.85rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.status-panel__item-value {
  color: #000000;
  font-size: 1.05rem;
  font-weight: 600;
  margin-top: 6px;
}

.status-panel__divider {
  width: 1px;
  height: 40px;
  @apply bg-white;
}

.status-panel__actions {
  margin-top: 18px;
  display: flex;
  justify-content: center;
}

.status-panel__cta {
  @apply bg-main-primary text-white shadow-lg;
  padding: 10px 22px;
  border-radius: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.status-panel__cta:hover {
  @apply bg-main-primary shadow-xl opacity-80;
  transform: translateY(-1px);
}

.status-panel__cta--disabled {
  @apply bg-gray-300 text-black;
  box-shadow: none;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .status-panel {
    padding: 18px 16px;
  }

  .status-panel__body {
    grid-template-columns: 1fr;
  }

  .status-panel__divider {
    width: 100%;
    height: 1px;
  }

  .status-panel__actions {
    justify-content: center;
  }
}

/* 填寫紀錄區域 */
.record-title {
  height: 6vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 0;
  padding: 0;
  scroll-margin-top: 20px;
}

.records-container {
  height: 94vh;
  display: flex;
  flex-direction: column;
}

/* 雷達圖區域 */
.radar-section-wrapper {
  height: 53.19%; /* 50/94 = 53.19% */
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}

.radar-section {
  @apply bg-white border border-gray-200;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.radar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  @apply border-b-2 border-gray-100;
  flex-shrink: 0;
}

.radar-title {
  font-size: 1.15rem;
  font-weight: 700;
  @apply text-gray-800;
  letter-spacing: 0.02em;
}

.score-display {
  display: flex;
  align-items: center;
}

.score-value {
  font-size: 1.5rem;
  font-weight: 700;
  @apply text-main-primary;
  letter-spacing: 0.02em;
}

.radar-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 10px;
  overflow: hidden;
}

.chart-wrapper {
  width: 100%;
  height: 100%;
  max-width: 600px;
  max-height: 400px;
  position: relative;
  margin: 0 auto;
}

.radar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  z-index: 10;
}

.radar-loading,
.radar-empty {
  @apply text-gray-500;
  font-size: 1.1rem;
  text-align: center;
}

.radar-container canvas {
  width: 100% !important;
  height: 100% !important;
  display: block;
}

/* 表格區域 */
.table-wrapper {
  height: 42.55%; /* 40/94 = 42.55% */
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 16px;
}

/* Apple-style Table */
.apple-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  @apply border border-gray-200;
}

.apple-table thead tr {
  @apply bg-gray-50;
  position: sticky;
  top: 0;
  z-index: 10;
}

.apple-table thead th {
  padding: 12px 16px;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  @apply text-gray-700 border-b border-gray-200;
  letter-spacing: 0.02em;
}

.apple-table tbody tr.data-row {
  @apply bg-white;
  transition: all 0.2s ease;
  @apply border-b border-gray-100;
  cursor: pointer;
}

.apple-table tbody tr.data-row.updating {
  pointer-events: none;
  opacity: 0.6;
}

.apple-table tbody tr.data-row:hover {
  @apply bg-teal-100;
}

.apple-table tbody tr.data-row.selected {
  @apply bg-teal-300 border-l-4 border-main-primary;
}

.apple-table tbody tr.data-row.selected:hover {
  @apply bg-teal-100;
}

.apple-table tbody tr.data-row:last-of-type {
  border-bottom: none;
}

.apple-table tbody td {
  padding: 14px 16px;
  text-align: center;
  font-size: 1rem;
  @apply text-gray-800;
}

.apple-table tbody tr.selected td {
  font-weight: 600;
  @apply text-teal-900;
}

@media (max-width: 768px) {
  .record-title {
    height: auto;
    min-height: 8vh;
  }

  .records-container {
    height: auto;
    min-height: 80vh;
  }

  .radar-section-wrapper {
    height: auto;
    min-height: 360px;
  }

  .radar-section {
    padding: 14px;
  }

  .radar-container {
    padding: 8px 4px;
    min-height: 240px;
  }

  .chart-wrapper {
    max-width: 100%;
    max-height: none;
    height: 240px;
  }

  .table-wrapper {
    height: auto;
    max-height: 300px;
  }

  .radar-header {
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
  }

  .radar-title {
    font-size: 1rem;
  }

  .score-value {
    font-size: 1.3rem;
  }

  .apple-table thead th,
  .apple-table tbody td {
    padding: 10px 8px;
    font-size: 0.9rem;
  }
}
</style>
